import { useState, useEffect } from 'react';
import {
  List,
  useListContext,
  useDataProvider,
  useNotify,
  SearchInput,
  TopToolbar,
  FilterButton,
  useRefresh,
} from 'react-admin';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Lock as LockIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import { SettingInput } from '../../components/settings/SettingInput';

interface Setting {
  key: string;
  value: string;
  value_type: 'string' | 'number' | 'boolean' | 'json';
  category: string;
  description?: string;
  is_encrypted: boolean;
  updated_at?: string;
  updated_by?: number;
}

const settingsFilters = [
  <SearchInput
    key="search"
    source="search"
    alwaysOn
    placeholder="Search by key or description"
  />,
];

const SettingsListActions = () => (
  <TopToolbar>
    <FilterButton />
  </TopToolbar>
);

const SettingsContent = () => {
  const { data, isLoading } = useListContext<Setting>();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (data) {
      const initialValues: Record<string, string> = {};
      data.forEach((setting) => {
        initialValues[setting.key] = setting.value;
      });
      setEditValues(initialValues);
    }
  }, [data]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No settings found
        </Typography>
      </Box>
    );
  }

  // Group settings by category
  const groupedSettings = data.reduce(
    (acc, setting) => {
      const category = setting.category || 'Other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(setting);
      return acc;
    },
    {} as Record<string, Setting[]>
  );

  const categories = Object.keys(groupedSettings).sort();

  const handleValueChange = (key: string, value: string) => {
    setEditValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async (setting: Setting) => {
    setSavingKeys((prev) => new Set(prev).add(setting.key));

    try {
      await dataProvider.update('settings', {
        id: setting.key,
        data: { value: editValues[setting.key] },
        previousData: setting,
      });

      notify('Setting updated successfully', { type: 'success' });
      refresh();
    } catch (error: any) {
      notify(`Error updating setting: ${error.message}`, { type: 'error' });
    } finally {
      setSavingKeys((prev) => {
        const newSet = new Set(prev);
        newSet.delete(setting.key);
        return newSet;
      });
    }
  };

  const handleReset = async (setting: Setting) => {
    try {
      await dataProvider.delete('settings', {
        id: setting.key,
        previousData: setting,
      });

      notify('Setting reset to default', { type: 'success' });
      refresh();
    } catch (error: any) {
      notify(`Error resetting setting: ${error.message}`, { type: 'error' });
    }
  };

  const hasChanges = (key: string, originalValue: string) => {
    return editValues[key] !== originalValue;
  };

  return (
    <Box sx={{ p: 2 }}>
      {categories.map((category) => {
        const categorySettings = groupedSettings[category];

        return (
          <Accordion key={category} defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${category}-content`}
              id={`${category}-header`}
            >
              <Typography variant="h6" fontWeight="bold">
                {category.charAt(0).toUpperCase() + category.slice(1)} Settings (
                {categorySettings.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                {categorySettings.map((setting) => (
                  <Box
                    key={setting.key}
                    sx={{
                      mb: 3,
                      p: 2,
                      backgroundColor: 'background.paper',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {setting.key}
                          </Typography>
                          {setting.is_encrypted && (
                            <Tooltip title="Encrypted value">
                              <LockIcon fontSize="small" color="warning" />
                            </Tooltip>
                          )}
                        </Box>
                        <Chip
                          label={setting.value_type}
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                        {setting.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 1 }}
                          >
                            {setting.description}
                          </Typography>
                        )}
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <SettingInput
                          settingKey={setting.key}
                          value={editValues[setting.key] || ''}
                          valueType={setting.value_type}
                          onChange={(value) => handleValueChange(setting.key, value)}
                        />
                      </Grid>

                      <Grid item xs={12} md={3}>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1,
                            justifyContent: 'flex-end',
                          }}
                        >
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleSave(setting)}
                            disabled={
                              !hasChanges(setting.key, setting.value) ||
                              savingKeys.has(setting.key)
                            }
                          >
                            {savingKeys.has(setting.key) ? (
                              <CircularProgress size={20} />
                            ) : (
                              'Save'
                            )}
                          </Button>
                          <Tooltip title="Reset to default">
                            <IconButton
                              size="small"
                              onClick={() => handleReset(setting)}
                            >
                              <ResetIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        {setting.updated_at && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 1, textAlign: 'right' }}
                          >
                            Updated: {new Date(setting.updated_at).toLocaleString()}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export const SettingsList = () => (
  <List
    filters={settingsFilters}
    actions={<SettingsListActions />}
    perPage={1000}
    pagination={false}
    sort={{ field: 'category', order: 'ASC' }}
  >
    <SettingsContent />
  </List>
);
